import React, { useState, useEffect } from 'react';
import { workflowTemplatesApi, adminApi } from '../../api/admin';
import toast from 'react-hot-toast';
import {
  Package, Check, Clock, ChevronRight, X, GitBranch, ListChecks,
  Layout, Database, ArrowRight, AlertTriangle, Heart, Users,
  Briefcase, Globe, FileText, Trash2, ShoppingBag
} from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import { useSidebarStore } from '../../store/sidebarStore';

const ICON_MAP = {
  car: Briefcase, gavel: Briefcase, heart: Heart, users: Users,
  briefcase: Briefcase, globe: Globe, workflow: GitBranch,
  'file-plus': FileText, folder: Package,
};

const PRACTICE_LABELS = {
  personal_injury: 'Personal Injury',
  family_law: 'Family Law',
  immigration: 'Immigration',
  real_estate: 'Real Estate',
  general: 'General',
};

export default function TemplateStore() {
  const { triggerRefresh } = useSidebarStore();
  const [templates, setTemplates] = useState([]);
  const [recordTypes, setRecordTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState('browse');
  const [targetRT, setTargetRT] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [fieldResolutions, setFieldResolutions] = useState({});
  const [result, setResult] = useState(null);
  const [installs, setInstalls] = useState([]);
  const [uninstalling, setUninstalling] = useState(null);
  const [newRTName, setNewRTName] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tplData, rtData] = await Promise.all([
        workflowTemplatesApi.list(),
        adminApi.getRecordTypes()
      ]);
      setTemplates(tplData.templates);
      setRecordTypes(rtData.data.record_types || rtData.data || []);

      // Get all installs
      const allInstalls = [];
      for (const rt of (rtData.data.record_types || rtData.data || [])) {
        try {
          const instData = await workflowTemplatesApi.installed(rt.id);
          if (instData.installs?.length > 0) {
            allInstalls.push(...instData.installs.map(i => ({ ...i, record_type_name: rt.name })));
          }
        } catch (e) {}
      }
      setInstalls(allInstalls);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!targetRT) return toast.error('Select a record type');
    if (targetRT === '__new__' && !newRTName.trim()) return toast.error('Enter a record type name');
    setPreviewing(true);
    try {
      if (targetRT === '__new__') {
        // New record type — no existing fields, so no conflicts possible
        const tpl = templates.find(t => t.id === selected.id);
        setPreview({
          summary: {
            stages: selected.stage_count || 0,
            transitions: 0,
            tasks: selected.task_count || 0,
            new_screens: Number(selected.screen_count) || 0,
            new_fields: Number(selected.field_count) || 0,
            shared_fields: 0,
          },
          conflicts: [],
          reused_screens: [],
          new_screens: [],
        });
        setStep('preview');
      } else {
        const data = await workflowTemplatesApi.preview(selected.id, targetRT);
        setPreview(data);
        if (data.conflicts.length > 0) {
          const defaults = {};
          data.conflicts.forEach(c => { defaults[c.api_name] = 'use_existing'; });
          setFieldResolutions(defaults);
        }
        setStep('preview');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleInstall = async () => {
    setInstalling(true);
    setStep('installing');
    try {
      const rtId = targetRT === '__new__' ? null : targetRT;
      const rtName = targetRT === '__new__' ? newRTName.trim() : undefined;
      const data = await workflowTemplatesApi.install(selected.id, rtId, fieldResolutions, rtName);
      setResult(data);
      setStep('done');
      toast.success(data.template_name + ' installed!');
      triggerRefresh();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Install failed');
      setStep('preview');
    } finally {
      setInstalling(false);
    }
  };

  const handleUninstall = async (install) => {
    if (!window.confirm('Uninstall "' + install.template_name + '" from ' + install.record_type_name + '? This will remove the workflow, stages, transitions, tasks, and any screens/fields that were created during installation.')) return;
    setUninstalling(install.id);
    try {
      await workflowTemplatesApi.uninstall(install.id);
      toast.success('Uninstalled ' + install.template_name);
      triggerRefresh();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Uninstall failed');
    } finally {
      setUninstalling(null);
    }
  };

  const resetModal = () => {
    setSelected(null); setStep('browse'); setTargetRT(''); setPreview(null);
    setFieldResolutions({}); setResult(null); setNewRTName('');
  };

  const practices = ['all', ...new Set(templates.map(t => t.practice_area))];
  const filtered = filter === 'all' ? templates : templates.filter(t => t.practice_area === filter);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Workflow Templates" description="Pre-built workflows with stages, tasks, screens, and fields — install onto any record type" icon={ShoppingBag} iconColor="text-purple-600" iconBg="bg-purple-50" />

      {/* Installed section */}
      {installs.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Installed Workflows</h2>
          <div className="space-y-2">
            {installs.map(install => (
              <div key={install.id} className="bg-white border border-green-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: (install.color || '#22C55E') + '15' }}>
                    <Check className="h-4 w-4" style={{ color: install.color || '#22C55E' }} />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">{install.template_name}</span>
                    <span className="text-xs text-gray-400 ml-2">on {install.record_type_name}</span>
                    {install.latest_version > (install.installed_version || 1) && (
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full"
                        title={`Version ${install.latest_version} available. Install again to add the updated workflow.`}>
                        v{install.latest_version} Available
                      </span>
                    )}
                    <div className="text-[11px] text-gray-400">
                      Installed {new Date(install.installed_at).toLocaleDateString()}
                      {install.installed_version && <span> (v{install.installed_version})</span>}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleUninstall(install)}
                  disabled={uninstalling === install.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-3 w-3" /> {uninstalling === install.id ? 'Removing...' : 'Uninstall'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {practices.map(p => (
          <button key={p} onClick={() => setFilter(p)}
            className={'px-3 py-1.5 text-xs font-medium rounded-full transition-colors ' +
              (filter === p ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
            {p === 'all' ? 'All' : PRACTICE_LABELS[p] || p}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(tpl => {
          const Icon = ICON_MAP[tpl.icon] || GitBranch;
          const features = typeof tpl.features === 'string' ? JSON.parse(tpl.features) : (tpl.features || []);
          return (
            <div key={tpl.id}
              onClick={() => { setSelected(tpl); setStep('select_rt'); }}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-brand-300 hover:shadow-md cursor-pointer transition-all group">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: tpl.color + '15' }}>
                      <Icon className="h-5 w-5" style={{ color: tpl.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{tpl.name}</h3>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: tpl.color + '15', color: tpl.color }}>
                        {PRACTICE_LABELS[tpl.practice_area] || tpl.practice_area}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 transition-colors mt-1" />
                </div>
                <p className="text-sm text-gray-500 mb-3">{tpl.description}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1"><GitBranch className="h-3 w-3" /> {tpl.stage_count} stages</span>
                  <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {tpl.task_count || 0} tasks</span>
                  {Number(tpl.screen_count) > 0 && <span className="flex items-center gap-1"><Layout className="h-3 w-3" /> {tpl.screen_count} screens</span>}
                  {Number(tpl.field_count) > 0 && <span className="flex items-center gap-1"><Database className="h-3 w-3" /> {tpl.field_count} fields</span>}
                </div>
              </div>
              {features.length > 0 && (
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {features.slice(0, 3).map((f, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-gray-500">
                        <Check className="h-3 w-3 text-green-500" /> {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">No workflow templates in this category yet.</div>
      )}

      {/* Install Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={resetModal}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: selected.color + '15' }}>
                  {React.createElement(ICON_MAP[selected.icon] || GitBranch, { className: "h-4 w-4", style: { color: selected.color } })}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {step === 'done' ? 'Installation Complete' : 'Install ' + selected.name}
                  </h2>
                  <p className="text-xs text-gray-400">
                    {step === 'select_rt' ? 'Choose a record type to install on' :
                     step === 'preview' ? 'Review what will be installed' :
                     step === 'installing' ? 'Installing...' : 'Workflow added successfully'}
                  </p>
                </div>
              </div>
              <button onClick={resetModal} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Select Record Type */}
              {step === 'select_rt' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Install on Record Type</label>
                    <select value={targetRT} onChange={e => {
                      setTargetRT(e.target.value);
                      if (e.target.value === '__new__' && !newRTName) {
                        setNewRTName(selected?.name || '');
                      }
                    }}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                      <option value="">Select a record type...</option>
                      <option value="__new__">+ Create new record type...</option>
                      {recordTypes.filter(rt => !rt.is_system).map(rt => (
                        <option key={rt.id} value={rt.id}>{rt.name}</option>
                      ))}
                    </select>
                    {targetRT === '__new__' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Record Type Name</label>
                        <input type="text" value={newRTName} onChange={e => setNewRTName(e.target.value)}
                          placeholder="e.g. MVA Case"
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                      </div>
                    )}
                    {targetRT !== '__new__' && recordTypes.some(rt => rt.is_system) && (
                      <p className="text-xs text-gray-400 mt-1.5">System record types (Contact, Company) cannot be modified by templates.</p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={resetModal}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
                      Cancel
                    </button>
                    <button onClick={handlePreview} disabled={!targetRT || (targetRT === '__new__' && !newRTName.trim()) || previewing}
                      className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      {previewing ? <><Clock className="h-4 w-4 animate-spin" /> Analyzing...</> : 'Continue'}
                    </button>
                  </div>
                </div>
              )}

              {/* Preview */}
              {step === 'preview' && preview && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">What will be installed</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Stages', count: preview.summary.stages, icon: GitBranch, color: '#6366F1' },
                        { label: 'Transitions', count: preview.summary.transitions, icon: ArrowRight, color: '#8B5CF6' },
                        { label: 'Tasks', count: preview.summary.tasks, icon: ListChecks, color: '#3B82F6' },
                        { label: 'New Screens', count: preview.summary.new_screens, icon: Layout, color: '#14B8A6' },
                        { label: 'New Fields', count: preview.summary.new_fields, icon: Database, color: '#F59E0B' },
                        { label: 'Reused Fields', count: preview.summary.shared_fields, icon: Check, color: '#22C55E' },
                      ].map(item => (
                        <div key={item.label} className="bg-gray-50 rounded-lg p-3 text-center">
                          <item.icon className="h-4 w-4 mx-auto mb-1" style={{ color: item.color }} />
                          <div className="text-lg font-bold text-gray-900">{item.count}</div>
                          <div className="text-[11px] text-gray-500">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {preview.reused_screens.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Shared Screens</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {preview.reused_screens.map(s => (
                          <span key={s.slug} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-md font-medium">{s.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.new_screens.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">New Screens</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {preview.new_screens.map(s => (
                          <span key={s.slug} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-medium">{s.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.conflicts.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <h3 className="text-sm font-semibold text-amber-800">{preview.conflicts.length} Field Conflict{preview.conflicts.length > 1 ? 's' : ''}</h3>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">These fields have the same name but different types:</p>
                      <div className="space-y-3">
                        {preview.conflicts.map(c => (
                          <div key={c.api_name} className="bg-white rounded-lg p-3 border border-amber-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900">{c.template_label} <span className="text-xs text-gray-400">({c.api_name})</span></span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                              <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-mono">{c.existing_type}</span>
                              <ArrowRight className="h-3 w-3" />
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-mono">{c.template_type}</span>
                            </div>
                            <div className="flex gap-2">
                              {['use_existing', 'create_new', 'skip'].map(val => (
                                <button key={val} onClick={() => setFieldResolutions(prev => ({ ...prev, [c.api_name]: val }))}
                                  className={'flex-1 py-1.5 px-2 rounded-md text-xs font-medium border transition-colors ' +
                                    (fieldResolutions[c.api_name] === val
                                      ? 'bg-brand-50 border-brand-300 text-brand-700'
                                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')}>
                                  {val === 'use_existing' ? 'Keep Existing' : val === 'create_new' ? 'Create New' : 'Skip'}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setStep('select_rt')}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">Back</button>
                    <button onClick={handleInstall}
                      className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 flex items-center justify-center gap-2">
                      <Package className="h-4 w-4" /> Install {selected.name}
                    </button>
                  </div>
                </div>
              )}

              {/* Installing */}
              {step === 'installing' && (
                <div className="flex flex-col items-center py-16">
                  <Clock className="h-10 w-10 text-brand-600 animate-spin mb-4" />
                  <p className="text-sm text-gray-500">Installing {selected.name}...</p>
                </div>
              )}

              {/* Done */}
              {step === 'done' && result && (
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{result.template_name} Installed!</h3>
                  <p className="text-sm text-gray-500 mb-6">Workflow added to your record type.</p>
                  <div className="grid grid-cols-3 gap-3 mb-6 max-w-sm mx-auto">
                    {Object.entries(result.resources).filter(([,v]) => v > 0).map(([k, v]) => (
                      <div key={k} className="bg-gray-50 rounded-lg p-2">
                        <div className="text-base font-bold text-gray-900">{v}</div>
                        <div className="text-[11px] text-gray-500">{k.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={resetModal}
                    className="w-full py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700">Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
